import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Alert, AlertDescription } from './ui/alert';
import { Eye, Shield } from 'lucide-react';

/**
 * PermissionBanner - Displays a banner when user has limited permissions
 * 
 * Props:
 * - show: boolean - Whether to show the banner (default: based on isViewer)
 * - message: string - Custom message to display
 * - type: 'viewer' | 'admin-only' - Type of restriction
 */
export const PermissionBanner = ({ 
  show, 
  message, 
  type = 'viewer' 
}) => {
  const { isViewer, isAdmin } = useAuth();
  
  // Determine if banner should show
  const shouldShow = show !== undefined ? show : (type === 'viewer' ? isViewer : !isAdmin);
  
  if (!shouldShow) return null;
  
  const defaultMessages = {
    'viewer': 'You have read-only access. Contact an admin to request edit permissions.',
    'admin-only': 'This section requires admin privileges.'
  };
  
  const displayMessage = message || defaultMessages[type];
  const Icon = type === 'viewer' ? Eye : Shield;
  
  return (
    <Alert className="mb-4 bg-amber-50 border-amber-200 text-amber-800">
      <Icon className="h-4 w-4" />
      <AlertDescription className="ml-2">
        {displayMessage}
      </AlertDescription>
    </Alert>
  );
};

/**
 * ViewerBadge - Small inline badge indicating viewer status
 */
export const ViewerBadge = () => {
  const { isViewer } = useAuth();
  
  if (!isViewer) return null;
  
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
      <Eye size={12} />
      View Only
    </span>
  );
};

export default PermissionBanner;
